import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import {
  BehaviorSubject,
  Observable,
  switchMap,
  take,
  takeUntil,
  throwError
} from 'rxjs'
import { catchError, filter } from 'rxjs/operators'
import { ErrorState } from '../../store/error/error.state'
import { AuthState } from '../../store/auth/auth.state'
import { UserState } from '../../store/user/user.state'
import { DepartmentState } from '../../store/department/department.state'
import { PccState } from '../../store/pcc/pcc.state'
import { BackendService } from '../../shared/services/backend.service'
import { BootstrapConfigService } from '../../shared/services/bootstrap-config.service'
import { SKIP_GLOBAL_ERROR_TOAST } from './skip-global-error.context'

@Injectable({
  providedIn: 'root'
})
export class RequestInterceptor implements HttpInterceptor {
  private readonly errorState = inject(ErrorState)
  private readonly authState = inject(AuthState)
  private readonly userState = inject(UserState)
  private readonly departmentState = inject(DepartmentState)
  private readonly pccState = inject(PccState)
  private readonly backendService = inject(BackendService)
  private readonly bootstrapConfigService = inject(BootstrapConfigService)

  private isRefreshing = false
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  )

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return next.handle(request).pipe(
      takeUntil(this.backendService.logoutObservable),
      catchError((errorResponse: HttpErrorResponse) => {
        const type = errorResponse.url?.split('/')
        if (
          errorResponse.status === 401 &&
          errorResponse.error &&
          errorResponse.error.code === 'MFA_VERIFICATION_CODE_INCORRECT'
        ) {
          this.errorState.setError(errorResponse.error)
          return throwError(errorResponse)
        } else if (
          errorResponse.url &&
          type &&
          type[type.length - 1] === 'refresh' &&
          errorResponse.status === 401
        ) {
          if (this.authState.isAuthenticated()) {
            this.authState.logout()
          }
          return throwError(errorResponse)
        } else if (errorResponse.status === 401) {
          this.isRefreshing = false
          return this.handle401Error(request, next)
        } else if (
          errorResponse.error &&
          (errorResponse.error.code === 'TOKEN_NOT_VALID' ||
            (errorResponse.error.code === 'LOGIN_FAILED' &&
              !errorResponse.url?.includes(
                this.bootstrapConfigService.loginUrl()
              )) ||
            errorResponse.error.code === 'REFRESH_TOKEN_NOT_VALID')
        ) {
          this.errorState.setError(errorResponse.error)
          this.authState.logout()
          return throwError(() => errorResponse)
        } else if (
          errorResponse.error &&
          errorResponse.error.code === 'ACCESS_DENIED'
        ) {
          return throwError(() => errorResponse)
        } else if (request.context.get(SKIP_GLOBAL_ERROR_TOAST)) {
          // The caller shows its own error UI (the Personal Assistant); no global toast on top of it.
          return throwError(() => errorResponse)
        } else {
          if (errorResponse.error && errorResponse.error.errors) {
            if (errorResponse.error.errors.length > 1) {
              let details: any
              errorResponse.error.errors.forEach((e: any) => {
                if (!details) details = `${e.detail}`
                else details = `${details}, ${e.detail}`
              })
              this.errorState.setError({
                ...errorResponse.error.errors[0],
                message: details
              })
            } else {
              this.errorState.setError({
                ...errorResponse.error.errors[0],
                message: errorResponse.error.errors[0].detail
              })
            }
          } else {
            this.errorState.setError(errorResponse.error)
          }
          return throwError(errorResponse)
        }
      })
    )
  }

  addAuthenticationToken(request: HttpRequest<any>, token: string) {
    if (!token) {
      return request
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    })
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true
      return this.authState.refreshAccessToken(false).pipe(
        takeUntil(this.backendService.logoutObservable),
        switchMap(token => {
          this.refreshTokenSubject.next(token)
          this.isRefreshing = false
          return next.handle(this.addAuthenticationToken(request, token))
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          this.errorState.setError(errorResponse.error)
          this.isRefreshing = false
          if (this.authState.isAuthenticated()) {
            this.authState.logout()
          }
          return next.handle(request)
        })
      )
    }
    return this.refreshTokenSubject.pipe(
      takeUntil(this.backendService.logoutObservable),
      filter(token => token !== null),
      take(1),
      switchMap(token =>
        next.handle(this.addAuthenticationToken(request, token))
      )
    )
  }
}
